import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { Card } from 'src/design/atoms/Card';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { CollapsiblePaperSection } from 'src/design/molecules/CollapsiblePaperSection';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import React from 'react';
import { GroupingWarningDto, LineGroupingDto } from 'src/api/FiretrolApiClient';

export interface IProcessingWarningsPanelProps {
  grouping: LineGroupingDto | null | undefined;
  isError: boolean;
  onRetry: () => void;
}

const isNote = (w: GroupingWarningDto) => w.severity === 'Note';

const plural = (n: number, word: string) => `${n} ${word}${n !== 1 ? 's' : ''}`;

const WarningSection: React.FC<{
  title: string;
  color: string;
  items: GroupingWarningDto[];
}> = ({ title, color, items }) => (
  <Stack gap="xs">
    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
      {title}
    </Text>
    {items.map((w, i) => (
      <Card key={`${w.kind}-${i}`} withBorder p="xs" radius="sm">
        <Group gap="xs" mb={4} wrap="nowrap">
          <Badge
            color={color}
            size="sm"
            variant="light"
            style={{ flexShrink: 0 }}
          >
            {title.slice(0, -1)}
          </Badge>
          {w.lineName && (
            <Badge
              color="gray"
              size="sm"
              variant="outline"
              style={{ flexShrink: 0 }}
            >
              {w.lineName}
            </Badge>
          )}
          <Text
            size="xs"
            c="dimmed"
            ff="monospace"
            miw={0}
            style={{ overflowWrap: 'anywhere' }}
          >
            {w.kind}
          </Text>
        </Group>
        <Text size="sm">{w.message}</Text>
      </Card>
    ))}
  </Stack>
);

export const ProcessingWarningsPanel: React.FC<
  IProcessingWarningsPanelProps
> = ({ grouping, isError, onRetry }) => {
  if (isError) {
    return (
      <Alert
        color="red"
        title="Could not load processing results"
        icon={<IconAlertTriangle size={16} />}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Text size="sm">
            The processing warnings for this design model could not be
            retrieved.
          </Text>
          <BasicButton variant="light" size="xs" onClick={onRetry}>
            Retry
          </BasicButton>
        </Group>
      </Alert>
    );
  }

  if (!grouping) return null;

  const warnings = grouping.warnings.filter((w) => !isNote(w));
  const notes = grouping.warnings.filter(isNote);
  const empty = warnings.length === 0 && notes.length === 0;

  return (
    <CollapsiblePaperSection
      title="Processing Warnings & Notes"
      trailing={
        <Group gap="xs">
          {warnings.length > 0 && (
            <Badge color="orange" leftSection={<IconAlertTriangle size={12} />}>
              {plural(warnings.length, 'warning')}
            </Badge>
          )}
          {notes.length > 0 && (
            <Badge color="blue" leftSection={<IconInfoCircle size={12} />}>
              {plural(notes.length, 'note')}
            </Badge>
          )}
        </Group>
      }
    >
      {empty ? (
        <Text size="sm" c="dimmed">
          No warnings or notes from processing.
        </Text>
      ) : (
        <Stack gap="md">
          {warnings.length > 0 && (
            <WarningSection title="Warnings" color="orange" items={warnings} />
          )}
          {notes.length > 0 && (
            <WarningSection title="Notes" color="blue" items={notes} />
          )}
        </Stack>
      )}
    </CollapsiblePaperSection>
  );
};
